import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { motion } from 'framer-motion';
import { Edit, Trash2, Eye, FileText, Play, Pause } from 'lucide-react';
import styles from './MisDocumentacionesObra.module.scss';
import EmptyState from '../../../UI/EmptyState/EmptyState';
import { ReactComponent as Search } from '../../../../assets/svg/misProyectosLittle.svg';
import { ReactComponent as EmptyDocumentIcon } from '../../../../assets/svg/servicesIcon.svg';
import { fetchUserProjectDocumentations, deleteProjectDocumentation } from '../../../../store/actions/projectDocumentationActions';
import { openMainModal } from '../../../../store/actions';
import environment from '../../../../environments/index';
import { projectDocumentationService } from '../../../../services/projectDocumentationService.ts';
import { buildNavigationParams } from '../../../../utils/navigationHelpers.ts';
import RegisteredUserProfileLoader from '../../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader';

interface ProjectDocumentation {
  id: number;
  name: string;
  description: string;
  stage: string;
  state: string | null;
  pause?: boolean;
  subCategory?: {
    name: string;
    category?: {
      name: string;
    };
  };
  use?: {
    name: string;
  };
  materiality?: {
    name: string;
  };
  budget?: number;
  budget_currency?: string;
  price?: number;
  price_currency?: string;
  consultation_price?: number;
  consultation_price_currency?: string;
  total_surface?: number;
  environments?: number;
  bedrooms?: number;
  bathrooms?: number;
  created_at?: string;
  updated_at?: string;
}

interface MisDocumentacionesObraProps {
  documentations: ProjectDocumentation[];
  userDocumentationsLoading: boolean;
  myPerson: boolean;
  userData: any;
  profileVisualizerData: any;
  setInfoReady: (ready: boolean) => void;
  fetchUserProjectDocumentations: (userId?: number | string, forceRefetch?: boolean) => Promise<any>;
  deleteProjectDocumentation: (id: number) => Promise<any>;
  openMainModal: (config: any) => void;
  currentTab?: string;
}

const MisDocumentacionesObra: React.FC<MisDocumentacionesObraProps> = ({
  documentations,
  userDocumentationsLoading,
  myPerson,
  userData,
  profileVisualizerData,
  setInfoReady,
  fetchUserProjectDocumentations,
  deleteProjectDocumentation,
  openMainModal,
  currentTab
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pauseLoading, setPauseLoading] = useState<Record<number, boolean>>({});

  const userId = useMemo(() => {
    return myPerson ? userData?.id : (profileVisualizerData?.id || null);
  }, [myPerson, userData?.id, profileVisualizerData?.id]);

  const loadDocumentations = useCallback(async () => {
    if (!userId) return;
    
    try {
      await fetchUserProjectDocumentations(userId);
    } catch (error) {
      console.error('Error loading documentations:', error);
    }
  }, [userId, fetchUserProjectDocumentations]);

  useEffect(() => {
    loadDocumentations();
  }, [loadDocumentations]);

  // Recargar documentaciones cuando cambia la ubicación (vuelve de editar)
  useEffect(() => {
    if (userId) {
      loadDocumentations();
    }
  }, [location.pathname, userId, loadDocumentations]);

  useEffect(() => {
    if (!userDocumentationsLoading && documentations !== undefined) {
      setInfoReady(true);
    }
  }, [userDocumentationsLoading, documentations, setInfoReady]);

  const handleEdit = (id: number) => {
    const isFromProfile = location.pathname.startsWith('/perfil/');
    if (isFromProfile && myPerson && userData && currentTab) {
      const userName = userData.type === "COMPANY" 
        ? userData.razon_social 
        : `${userData.name} ${userData.last_name}`;
      const params = buildNavigationParams(currentTab as string, String(userData.id), userName);
      navigate(`/documentacion-de-obra/edit/${id}${params}`);
    } else {
      navigate(`/documentacion-de-obra/edit/${id}`);
    }
  };

  const handleDelete = (id: number, name: string) => {
    openMainModal({
      title: '¿Estás seguro que deseas eliminar esta documentación de obra?',
      body: `Una vez eliminada, la documentación "${name}" no podrá ser recuperada.`,
      iconType: 'warning',
      confirmHandler: async () => {
        try {
          await deleteProjectDocumentation(id);
          if (userId) {
            await fetchUserProjectDocumentations(userId);
          }
          openMainModal({
            title: '¡Eliminado!',
            body: 'Tu documentación de obra se ha eliminado con éxito',
            iconType: 'success',
            isSuccess: true,
            confirmHandler: null,
          });
        } catch (error: any) {
          console.error('Error deleting documentation:', error);
          openMainModal({
            title: '¡Ups!',
            body: 'Se produjo un error al eliminar la documentación, intente nuevamente o escribanos a contacto@edify.la ¡Muchas gracias!',
            isSuccess: false,
            confirmHandler: null,
            iconType: 'error',
          });
        }
      },
    });
  };

  const handleTogglePause = async (id: number, currentPause: boolean) => {
    setPauseLoading({ ...pauseLoading, [id]: true });
    try {
      await projectDocumentationService.updateProjectDocumentation(String(id), { pause: !currentPause });
      if (userId) {
        await fetchUserProjectDocumentations(userId, true);
      }
    } catch (error: any) {
      console.error('Error toggling pause:', error);
      openMainModal({
        title: 'Error',
        body: 'No se pudo cambiar el estado de publicación. Intente nuevamente.',
        isSuccess: false,
        confirmHandler: null,
        iconType: 'error',
      });
    } finally {
      setPauseLoading({ ...pauseLoading, [id]: false });
    }
  };

  const handleViewPreview = (id: number) => {
    const marketplaceUrl = environment.marketplace.url;
    const previewUrl = `${marketplaceUrl}/proyecto/${id}`;
    window.open(previewUrl, '_blank');
  };

  const getStateLabel = (state: string | null): string => {
    if (!state || state === '') return 'Sin estado';
    const stateMap: { [key: string]: string } = {
      'pending': 'Pendiente',
      'approved': 'Aprobado',
      'refused': 'Rechazado',
      'pendiente': 'Pendiente',
      'aprobado': 'Aprobado',
      'rechazado': 'Rechazado'
    };
    return stateMap[state.toLowerCase()] || state;
  };

  const getStateClass = (state: string | null): string => {
    if (!state || state === '') return styles.status_sinEstado;
    const normalizedState = state.toLowerCase();
    if (normalizedState === 'pending' || normalizedState === 'pendiente') return styles.status_pendiente;
    if (normalizedState === 'approved' || normalizedState === 'aprobado') return styles.status_aprobado;
    if (normalizedState === 'refused' || normalizedState === 'rechazado') return styles.status_rechazado;
    return styles.status_sinEstado;
  };

  const formatCurrency = (amount: number | undefined, currency: string | undefined): string => {
    if (!amount) return 'No especificado';
    const formattedAmount = new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
    return `${currency === 'USD' ? 'US$' : '$'} ${formattedAmount}`;
  };

  const formatDate = (date: string | undefined): string => {
    if (!date) return '';
    const utcDate = new Date(date);
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    return utcDate.toLocaleDateString('es-ES', options);
  };

  const sortedDocumentations = useMemo(() => {
    if (!documentations || documentations.length === 0) return [];
    return [...documentations].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [documentations]);

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles.container}
    >
      {userDocumentationsLoading ? (
        <div className={styles.loadingContainer}>
          <RegisteredUserProfileLoader smallVersion={false} />
        </div>
      ) : (
        sortedDocumentations && sortedDocumentations.length > 0 ? (
          <div className={styles.container_body}>
            {sortedDocumentations.map((doc) => (
            <div key={doc.id} className={styles.documentationCard}>
              <div className={styles.documentationCard_header}>
                <div className={styles.documentationCard_header_left}>
                  <h3 className={styles.documentationCard_title}>{doc.name}</h3>
                  <span className={`${styles.documentationCard_status} ${getStateClass(doc.state)}`}>
                    {getStateLabel(doc.state)}
                  </span>
                </div>
                {myPerson && (
                  <div className={styles.documentationCard_header_actions}>
                    <button
                      className={`${styles.actionButton} ${doc.pause ? styles.actionButton_paused : ''}`}
                      onClick={() => handleTogglePause(doc.id, doc.pause || false)}
                      title={doc.pause ? 'Reanudar publicación' : 'Pausar publicación'}
                      disabled={pauseLoading[doc.id]}
                    >
                      {pauseLoading[doc.id] ? (
                        <span className={styles.loadingSpinner}></span>
                      ) : doc.pause ? (
                        <Play size={16} />
                      ) : (
                        <Pause size={16} />
                      )}
                    </button>
                    <button
                      className={styles.actionButton}
                      onClick={() => handleEdit(doc.id)}
                      title="Editar"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.actionButton_delete}`}
                      onClick={() => handleDelete(doc.id, doc.name)}
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.documentationCard_body}>
                <div className={styles.documentationCard_info}>
                  <p className={styles.documentationCard_description}>
                    {doc.description || 'Sin descripción'}
                  </p>

                  <div className={styles.documentationCard_details}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Tipo:</span>
                      <span className={styles.detailValue}>
                        {doc.stage === 'proyecto' ? 'Proyecto' : doc.stage === 'anteproyecto' ? 'Anteproyecto' : doc.stage || 'No especificado'}
                      </span>
                    </div>

                    {doc.subCategory && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Categoría:</span>
                        <span className={styles.detailValue}>
                          {doc.subCategory.category?.name ? `${doc.subCategory.category.name} - ` : ''}
                          {doc.subCategory.name}
                        </span>
                      </div>
                    )}

                    {doc.use && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Uso:</span>
                        <span className={styles.detailValue}>{doc.use.name}</span>
                      </div>
                    )}

                    {doc.materiality && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Materialidad:</span>
                        <span className={styles.detailValue}>{doc.materiality.name}</span>
                      </div>
                    )}

                    {doc.total_surface && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Superficie total:</span>
                        <span className={styles.detailValue}>{doc.total_surface} m²</span>
                      </div>
                    )}

                    {(doc.environments || doc.bedrooms || doc.bathrooms) && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Características:</span>
                        <span className={styles.detailValue}>
                          {[
                            doc.environments ? `${doc.environments} amb.` : null,
                            doc.bedrooms ? `${doc.bedrooms} dorm.` : null,
                            doc.bathrooms ? `${doc.bathrooms} baños` : null
                          ].filter(Boolean).join(' • ')}
                        </span>
                      </div>
                    )}

                    {doc.price && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Precio de venta:</span>
                        <span className={styles.detailValue}>
                          {formatCurrency(doc.price, doc.price_currency)}
                        </span>
                      </div>
                    )}

                    {doc.consultation_price && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Precio de consulta:</span>
                        <span className={styles.detailValue}>
                          {formatCurrency(doc.consultation_price, doc.consultation_price_currency)}
                        </span>
                      </div>
                    )}

                    {doc.budget && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Presupuesto:</span>
                        <span className={styles.detailValue}>
                          {formatCurrency(doc.budget, doc.budget_currency)}
                        </span>
                      </div>
                    )}

                    {doc.created_at && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Creado:</span>
                        <span className={styles.detailValue}>{formatDate(doc.created_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.documentationCard_footer}>
                {doc.state === 'approved' || doc.state === 'aprobado' ? (
                  <button
                    className={styles.viewButton}
                    onClick={() => handleViewPreview(doc.id)}
                  >
                    <Eye size={16} />
                    Ver en marketplace
                  </button>
                ) : (
                  <div className={styles.pendingMessage}>
                    <FileText size={16} />
                    {doc.state === 'pending' || doc.state === 'pendiente'
                      ? 'En revisión'
                      : 'No disponible públicamente'}
                  </div>
                )}
              </div>
            </div>
          ))}
          </div>
        ) : (
          myPerson ? (
            <div className={styles.emptyState}>
              <EmptyState
                title="No tienes documentaciones de obra ¡Crea tu primera documentación!"
                icon={<EmptyDocumentIcon width={50} height={50} />}
                isPortfolio={false}
              />
            </div>
          ) : (
            <EmptyState
              title="Este usuario no tiene documentaciones de obra"
              icon={<Search width={50} height={50} />}
              isPortfolio={false}
            />
          )
        )
      )}
    </motion.section>
  );
};

const mapStateToProps = (state: any) => ({
  documentations: state.anteProyecto.userDocumentations || [],
  userDocumentationsLoading: state.anteProyecto.userDocumentationsLoading || false,
});

export default connect(mapStateToProps, {
  fetchUserProjectDocumentations,
  deleteProjectDocumentation,
  openMainModal,
})(MisDocumentacionesObra);

